import type { UploadedImage } from '$/hooks/useImageUpload';
import { setUploadErrorToast } from '$/store/useUploadErrorToastStore';
import { getVideoCoverSizeByFile } from '$/utils';
import { FileTypeEnum, getVideoCoverUrl, initializeFileProcessing, performSafetyCheck } from '$/utils/upload';
import type { CallbackFiles } from '$/utils/upload';

export type MediacheckUploadItem = {
  url: string;
  type: FileTypeEnum;
  fileName: string;
};

export type MediacheckUploadOptions = {
  isCheckSafe?: boolean;
};

export type VideoDurationInfo = {
  duration?: number;
  requestDuration?: number;
};

/**
 * 单工具上传默认跳过 mediacheck，避免 dev 资源同步/访问偶发导致上传失败。
 * 需要与画布 dealUploadFile 一致做上传后安审时，显式传 isCheckSafe: true。
 *
 * @returns 未开启安审时返回原 URL；开启安审时返回通过安审的 URL 列表（顺序与输入一致）
 */
export async function filterUrlsByMediacheck(
  items: MediacheckUploadItem[],
  options: MediacheckUploadOptions = {},
): Promise<string[]> {
  if (!items.length) return [];
  const { isCheckSafe = false } = options;

  if (!isCheckSafe) {
    return items.map((item) => item.url);
  }

  const { errorList } = initializeFileProcessing([]);
  const callbackFiles = items.map((item) => {
    const dot = item.fileName.lastIndexOf('.');
    const name = dot > 0 ? item.fileName.slice(0, dot) : item.fileName;
    const suffix = dot > 0 ? item.fileName.slice(dot + 1) : '';
    return {
      url: item.url,
      type: item.type,
      uri: '',
      name,
      suffix,
      coverUrl: '',
      source: 'ai_tools',
    } as CallbackFiles;
  });

  const safe = await performSafetyCheck(callbackFiles, errorList);

  if (errorList.checkSafeError.length > 0) {
    setUploadErrorToast(true, [{
      message: { key: 'RoboNeo_Web_upload_content_warning_text' },
      fileNum: errorList.checkSafeError.length,
    }]);
  }

  return safe.map((f) => f.url);
}

function getUploadedSourceFile(uploaded: UploadedImage, files: File[], index: number) {
  return uploaded.sourceFile ?? files[uploaded.sourceIndex ?? index];
}

export function getUploadedFileName(uploaded: UploadedImage, files: File[], index: number) {
  return getUploadedSourceFile(uploaded, files, index)?.name || `media-${index}`;
}

export function getUploadedMediaType(uploaded: UploadedImage, files: File[], index: number) {
  const file = getUploadedSourceFile(uploaded, files, index);
  if (file?.type.startsWith('video/') || /\.(mp4|mov|m4v)$/i.test(file?.name || '')) {
    return FileTypeEnum.Video;
  }
  return FileTypeEnum.Image;
}

/**
 * 将浏览器读取到的视频浮点时长转为展示口径秒数，避免 5.01 秒被 ceil 成 6 秒。
 */
export function normalizeVideoDuration(duration: number) {
  if (!Number.isFinite(duration) || duration <= 0) return undefined;
  return Math.max(1, Math.floor(duration));
}

/**
 * 将视频时长转为 cost 与 submit 接口参数口径，按计费规则向上取整。
 */
export function normalizeRequestVideoDuration(duration?: number) {
  const value = Number(duration);
  return Number.isFinite(value) && value > 0 ? Math.ceil(value) : 0;
}

/**
 * 读取上传视频的展示口径本地时长。
 */
export async function getUploadedVideoDuration(uploaded: UploadedImage, files: File[], index: number) {
  return (await getUploadedVideoDurationInfo(uploaded, files, index)).duration;
}

/**
 * 读取上传视频时长，分别保留展示口径和接口参数口径。
 */
export async function getUploadedVideoDurationInfo(
  uploaded: UploadedImage,
  files: File[],
  index: number,
): Promise<VideoDurationInfo> {
  if (getUploadedMediaType(uploaded, files, index) !== FileTypeEnum.Video) return {};
  const file = getUploadedSourceFile(uploaded, files, index);
  if (!file) return {};
  try {
    const info = await getVideoCoverSizeByFile(file);
    return {
      duration: normalizeVideoDuration(info.duration),
      requestDuration: info.duration,
    };
  } catch (error) {
    console.warn('读取视频时长失败:', error);
    return {};
  }
}

/**
 * 创建批量上传素材项，并在视频素材上保留展示时长和接口参数时长。
 */
export async function createUploadedMediaBatchItem(uploaded: UploadedImage, files: File[], index: number, id: string) {
  const type = getUploadedMediaType(uploaded, files, index);
  const durationInfo = await getUploadedVideoDurationInfo(uploaded, files, index);
  return {
    id,
    status: 'normal' as const,
    url: uploaded.url,
    fileName: getUploadedFileName(uploaded, files, index),
    type,
    coverUrl: type === FileTypeEnum.Video ? getVideoCoverUrl(uploaded.url) : '',
    duration: durationInfo.duration,
    requestDuration: durationInfo.requestDuration,
    selected: false,
  };
}

export async function filterUploadedByMediacheck(
  uploaded: UploadedImage[],
  files: File[],
  options: MediacheckUploadOptions = {},
) {
  const { isCheckSafe = false } = options;
  if (!isCheckSafe) return uploaded;

  const items = uploaded.map((img, index) => ({
    url: img.url,
    type: getUploadedMediaType(img, files, index),
    fileName: getUploadedFileName(img, files, index),
  }));
  const safeSet = new Set(await filterUrlsByMediacheck(items, { isCheckSafe }));
  return uploaded.filter((img) => safeSet.has(img.url));
}
